import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { MessageCircle } from "lucide-react";
const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Curriculum", href: "#curriculum" },
  { label: "Programmes", href: "#tracks" },
  { label: "Why Us", href: "#mission" },
];

const footerColumns = [
  {
    heading: "Programme",
    links: [
      { label: "Home", to: "/" },
      { label: "Curriculum", to: "/#curriculum" },
      { label: "Age groups", to: "/#tracks" },
      { label: "Why us", to: "/#mission" },
    ],
  },
  {
    heading: "Families",
    links: [
      { label: "Enrol a child", to: "/enroll" },
      { label: "Sign in", to: "/login" },
      { label: "Create an account", to: "/register" },
      { label: "Forgot password", to: "/forgot-password" },
    ],
  },
  {
    heading: "Help",
    links: [
      { label: "Contact us", to: "/contact" },
      { label: "Common questions", to: "/contact#faq" },
      { label: "hello@futuresmartlab.com", to: "mailto:hello@futuresmartlab.com" },
    ],
  },
];

export default function MainLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  // Links like /#curriculum or /contact#faq: scroll to the section once the
  // page has rendered; any other page change starts at the top.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    const t = window.setTimeout(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
    }, 60);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f1f3f8] font-sans text-[#16213e]">
      {/* ── NAV ── */}
      <nav className="sticky top-0 z-50 border-b border-[#dde2ec] bg-white/95 backdrop-blur-xl shadow-[0_1px_0_rgba(27,45,94,0.08)]">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-10">
          {/* Logo */}
          <Link to="/" className="flex flex-shrink-0 items-center gap-2.5">
            <img
              src="/images/logo-with-text.png"
              alt="Future Smart Lab"
              className="h-10 w-auto object-contain sm:h-11"
            />
          </Link>

          {/* Desktop links */}
          <div className="hidden items-center gap-1 md:flex">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={isHome ? l.href : `/${l.href}`}
                className="rounded-full px-4 py-2 text-sm font-medium text-[#3b4560] transition hover:bg-[#ebeff7] hover:text-[#1b2d5e]"
              >
                {l.label}
              </a>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden items-center gap-3 md:flex">
            <Link
              to="/login"
              className="rounded-full px-4 py-2 text-sm font-medium text-[#3b4560] transition hover:bg-[#ebeff7]"
            >
              Login
            </Link>
            <Link
              to="/enroll"
              className="rounded-full bg-gradient-to-r from-brand-600 to-brand-400 px-5 py-2.5 text-sm font-semibold text-white shadow-glow transition hover:-translate-y-0.5 hover:shadow-glow-lg"
            >
              Enroll Now
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 rounded-xl md:hidden"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <span className={`block h-0.5 w-5 bg-[#3b4560] transition-all ${menuOpen ? "translate-y-2 rotate-45" : ""}`} />
            <span className={`block h-0.5 w-5 bg-[#3b4560] transition-all ${menuOpen ? "opacity-0" : ""}`} />
            <span className={`block h-0.5 w-5 bg-[#3b4560] transition-all ${menuOpen ? "-translate-y-2 -rotate-45" : ""}`} />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="border-t border-[#e5e8ef] bg-white px-4 pb-5 pt-3 md:hidden">
            <div className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <a
                  key={l.label}
                  href={isHome ? l.href : `/${l.href}`}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-xl px-4 py-3 text-sm font-medium text-[#3b4560] hover:bg-[#ebeff7]"
                >
                  {l.label}
                </a>
              ))}
              <div className="mt-3 flex gap-3">
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="flex-1 rounded-full border border-brand-200 py-3 text-center text-sm font-semibold text-[#3b4560]"
                >
                  Login
                </Link>
                <Link
                  to="/enroll"
                  onClick={() => setMenuOpen(false)}
                  className="flex-1 rounded-full bg-gradient-to-r from-brand-600 to-brand-400 py-3 text-center text-sm font-semibold text-white shadow-glow"
                >
                  Enroll Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main>
        <Outlet />
      </main>

      {/* ── FOOTER ── */}
      <footer className="bg-[#16213e] text-white">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
          <div className="grid gap-12 md:grid-cols-[1.6fr_1fr_1fr_1fr]">
            {/* Brand */}
            <div>
              <Link to="/" className="inline-flex items-center gap-2.5">
                <img src="/images/logo.png" alt="" className="h-10 w-10 rounded-xl" />
                <span className="text-lg font-extrabold tracking-tight text-white">Future Smart Lab</span>
              </Link>
              <p className="mt-4 max-w-xs text-sm leading-7 text-white/55">
                Raising children who are prepared for prosperity, innovation, and
                solving tomorrow's problems.
              </p>
              <a
                href="https://wa.me/2347052499758"
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex rounded-full border border-[#c9a227]/50 px-4 py-2 text-xs font-semibold text-[#e3c76a] transition hover:border-[#c9a227] hover:text-white"
              >
                <MessageCircle className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Chat with us on WhatsApp
              </a>
            </div>

            {/* Links */}
            {footerColumns.map((col) => (
              <div key={col.heading}>
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
                  {col.heading}
                </p>
                <div className="flex flex-col gap-3">
                  {col.links.map((l) =>
                    l.to.startsWith("mailto:") ? (
                      <a key={l.label} href={l.to} className="text-sm text-white/60 transition hover:text-white">
                        {l.label}
                      </a>
                    ) : (
                      <Link key={l.label} to={l.to} className="text-sm text-white/60 transition hover:text-white">
                        {l.label}
                      </Link>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row">
            <p className="text-xs text-white/30">
              © {new Date().getFullYear()} Future Smart Lab Limited · Abuja, Nigeria
            </p>
            <p className="text-xs text-white/30">
              Raising builders, leaders, and innovators.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}